/**
 * SubscriptionBanner Component
 * 
 * Responsabilidade: Exibir avisos de assinatura no topo da página.
 * Usa o hook useSubscriptionAlert para lógica e o componente Banner para UI.
 * 
 * Princípios:
 * - Single Responsibility: Apenas decide quando/o que mostrar no banner
 * - DRY: Reutiliza Banner UI e hook de lógica
 * - ~60 linhas - bem abaixo do limite
 */

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import Banner, { BannerVariant } from '../../../components/ui/Banner';
import { useSubscriptionAlert } from '../hooks/useSubscriptionAlert';

// Mapeia severidade do hook para variant do Banner UI
const severityToVariant: Record<string, BannerVariant> = {
    info: 'info',
    warning: 'warning',
    critical: 'error',
    blocked: 'error',
};

const SubscriptionBanner: React.FC = () => {
    const { currentPage, setCurrentPage } = useAppContext();
    const { alert, shouldShowBanner } = useSubscriptionAlert();
    const [isDismissed, setIsDismissed] = useState(false);

    // Reset dismissed state when alert changes
    useEffect(() => {
        setIsDismissed(false);
    }, [alert?.severity, alert?.title]);

    // Não renderiza se não há alerta, foi fechado, ou status é blocked (tratado pelo Blocker)
    if (!shouldShowBanner || !alert || isDismissed || alert.severity === 'blocked') {
        return null;
    }

    const handleAction = () => {
        if (alert?.ctaLabel) setCurrentPage(alert.ctaPath);
    };
    const handleClose = alert.severity !== 'critical' ? () => setIsDismissed(true) : undefined;

    // Se o usuário já está na página de CTA, não exibe o botão
    const action = alert.ctaLabel && currentPage !== alert.ctaPath
        ? { label: alert.ctaLabel, onClick: handleAction }
        : undefined;

    return (
        <Banner
            variant={severityToVariant[alert.severity] || 'warning'}
            title={alert.title}
            description={alert.description}
            action={action}
            onClose={handleClose}
        />
    );
};

export default SubscriptionBanner;
